import { useMemo } from 'react';
import { useEmployees } from '../../hooks/useEmployees';
import { useTransactions } from '../../hooks/useTransactions';
import { useEmployeeDeductions } from '../../hooks/useEmployeeDeductions';
import { useTimesheets } from '../../hooks/useTimesheets';
import { countWorkedDays, daysInMonth, monthToApiDate } from '../Timesheet/timesheetUtils';

export interface SalaryReportRow {
  key: string;
  name: string;
  branchName: string;
  branchId: string;
  payType: 'monthly' | 'daily';
  salary: number; // monthly salary, or daily rate for daily-paid employees
  hasTimesheet: boolean;
  workedDays: number;
  earned: number;
  cashAdvances: number;
  deductions: number;
  net: number;
}

/** Out transactions whose category marks them as a cash advance (سلفة) */
const isCashAdvance = (t: any): boolean => {
  const name = t.category?.name || '';
  return /سلف|advance/i.test(name);
};

const matchesEmployee = (recipientId: any, emp: any): boolean => {
  if (!recipientId) return false;
  return recipientId === emp.documentId || String(recipientId) === String(emp.id);
};

/**
 * Salary report data: combines employees (salary), the month's timesheets
 * (worked days; تعيين/رفت = 0.5), cash-advance out transactions and employee
 * deductions. Earned pay depends on the employee's pay type:
 * monthly -> the full salary; daily -> daily rate * worked days.
 */
export function useSalaryReportData(month: string) {
  const from = monthToApiDate(month);
  const to = `${month}-${String(daysInMonth(month)).padStart(2, '0')}`;

  const employeesQO = useMemo(() => ({
    pagination: { pageSize: 500 },
    sort: [{ field: 'name', order: 'asc' as const }],
  }), []);
  const transactionsQO = useMemo(() => ({
    filters: { type: { $eq: 'out' }, date: { $gte: from, $lte: to } },
    pagination: { pageSize: 1000 },
  }), [from, to]);
  const deductionsQO = useMemo(() => ({
    filters: { date: { $gte: from, $lte: to } },
    pagination: { pageSize: 1000 },
  }), [from, to]);
  const timesheetsQO = useMemo(() => ({
    filters: { month: { $eq: from } },
    pagination: { pageSize: 500 },
  }), [from]);

  const { data: employees = [], isLoading: l1, error, refetch } = useEmployees(employeesQO);
  const { data: transactions = [], isLoading: l2 } = useTransactions(transactionsQO);
  const { data: deductions = [], isLoading: l3 } = useEmployeeDeductions(deductionsQO);
  const { data: timesheets = [], isLoading: l4 } = useTimesheets(timesheetsQO);

  const rows: SalaryReportRow[] = useMemo(() => {
    return (employees as any[]).map((emp: any) => {
      const key = emp.documentId || String(emp.id);
      const payType: 'monthly' | 'daily' = emp.payType === 'daily' ? 'daily' : 'monthly';
      const salary = payType === 'daily' ? (Number(emp.dailyRate) || 0) : (Number(emp.salary) || 0);

      const ts: any = (timesheets as any[]).find(
        (t: any) => t.employeeId === key || String(t.employeeId) === String(emp.id)
      );
      const workedDays = ts ? countWorkedDays(ts.days) : 0;
      const earned = payType === 'daily'
        ? Math.round(salary * workedDays * 100) / 100
        : salary;

      const cashAdvances = (transactions as any[])
        .filter((t: any) => t.recipientType === 'employee' && matchesEmployee(t.recipientId, emp) && isCashAdvance(t))
        .reduce((sum: number, t: any) => sum + (Number(t.amount) || 0), 0);

      const deductionsTotal = (deductions as any[])
        .filter((d: any) => matchesEmployee(d.employeeId, emp))
        .reduce((sum: number, d: any) => sum + (Number(d.amount) || 0), 0);

      return {
        key,
        name: emp.name || '-',
        branchName: emp.branch?.name || '-',
        branchId: emp.branchId || '',
        payType,
        salary,
        hasTimesheet: !!ts,
        workedDays,
        earned,
        cashAdvances,
        deductions: deductionsTotal,
        net: Math.round((earned - cashAdvances - deductionsTotal) * 100) / 100,
      };
    });
  }, [employees, transactions, deductions, timesheets]);

  return {
    rows,
    isLoading: l1 || l2 || l3 || l4,
    error,
    refetch,
  };
}
